import dayjs from 'dayjs'
import { ChartDataPoint, DataLayer } from 'features/systemLens'
import { DEFAULT_CHART_SETTINGS } from 'features/timeSeries/config/constants'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import {
  predictValues,
  RegressionResult,
} from 'features/timeSeries/regressionHelpers'
import { PlotData } from 'plotly.js'
import {
  EnhancedSensorEntry,
  RegressionConfig,
  SensorAxisMapping,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

import { resampleData } from './dataProcessing'

interface PlotlyTraceOptions {
  dataLayers: DataLayer[]
  chartData: ChartDataPoint[]
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  unitMapping: Record<string, string>
  sensorAxisMapping: SensorAxisMapping
  regressionConfigs: RegressionConfig[]
  regressionResults: Record<string, RegressionResult>
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  xAxisSensorId?: string | null
  connectPoints: boolean
  sensorVisibilityConfig: Record<string, boolean>
  customSensorColors?: Record<string, string>
  highlightedSensorId?: string | null
}

/**
 * Generate Plotly traces for sensor data
 */
export const generateSensorTraces = (
  options: PlotlyTraceOptions
): Partial<PlotData>[] => {
  const {
    dataLayers,
    chartData,
    allSensorsToRender,
    enhancedSensorEntries,
    sensorAxisMapping,
    xAxisSensorId,
    connectPoints,
    sensorVisibilityConfig,
    customSensorColors,
    derivedValues,
    unitMapping,
    highlightedSensorId,
  } = options

  const traces: Partial<PlotData>[] = []
  const processDataLayer = dataLayers.find((l) => l.id === 'process-data')

  if (!processDataLayer?.enabled || chartData.length === 0) {
    return traces
  }

  const xAxisSensor = enhancedSensorEntries.find((e) => e.id === xAxisSensorId)
  allSensorsToRender.forEach(({ id: sensorId, name }, index) => {
    if (sensorId === xAxisSensorId) return
    const visible = sensorVisibilityConfig?.[sensorId] ?? true

    const isDerived =
      sensorId.startsWith('derived_') || sensorId.startsWith('regression_')
    let displayName: string
    let unit: string

    if (isDerived) {
      const derivedValue = derivedValues.find((dv) => dv.id === sensorId)
      displayName = name || sensorId
      unit = derivedValue?.data.unit || 'No Unit'
    } else {
      const sensor = enhancedSensorEntries.find((e) => e.id === sensorId)
      if (!sensor) return
      displayName = sensor.data?.name || sensor.id
      unit = unitMapping[displayName] || 'No Unit'
    }

    const axisInfo = sensorAxisMapping[sensorId]

    const xValues: (string | number | null)[] = []
    const yValues: (number | null)[] = []

    if (xAxisSensorId) {
      // Use sensor values for x-axis
      chartData.forEach((point) => {
        const xValue = point[xAxisSensorId]
        const yValue = point[sensorId]
        if (typeof xValue === 'number' && typeof yValue === 'number') {
          xValues.push(xValue)
          yValues.push(yValue)
        }
      })
    } else {
      // Use timestamps for x-axis
      chartData.forEach((point) => {
        if (
          point.time !== null &&
          point.time !== undefined &&
          typeof point[sensorId] === 'number'
        ) {
          xValues.push(point.time)
          const value = point[sensorId]
          yValues.push(value)
        }
      })
    }

    const padding = '&nbsp;'.repeat(
      Math.max(0, DEFAULT_CHART_SETTINGS.MAX_NAME_LENGTH - displayName.length)
    )

    const lineColor = customSensorColors?.[sensorId] || getSensorColor(index)

    const isHighlighted = highlightedSensorId === sensorId
    const hasHighlight =
      highlightedSensorId !== null && highlightedSensorId !== undefined
    const opacity = hasHighlight ? (isHighlighted ? 1 : 0.3) : 1
    const lineWidth = isHighlighted ? 3 : 2
    const markerSize = connectPoints ? 0 : isHighlighted ? 8 : 6

    // Resample data for performance
    const { x: resampledX, y: resampledY } = resampleData(
      xValues,
      yValues,
      DEFAULT_CHART_SETTINGS.RESAMPLE_MAX_POINTS
    )

    traces.push({
      type: 'scattergl',
      mode: connectPoints ? 'lines+markers' : 'markers',
      name: displayName,
      x: resampledX,
      y: resampledY,
      yaxis: axisInfo?.axis || 'y',
      line: {
        color: lineColor,
        width: lineWidth,
      },
      marker: {
        color: lineColor,
        size: markerSize,
      },
      opacity: opacity,
      connectgaps: true,
      hovertemplate:
        `<b style="color:${lineColor}; font-family: monospace">${displayName}:${padding}</b> ` +
        `%{y:.5f}${unit && unit !== 'No Unit' ? ` ${unit}` : ''}` +
        (xAxisSensor ? `<br>${xAxisSensor.data.name}: %{x:.5f}` : '') +
        '<extra></extra>',
      visible: visible,
    })
  })

  return traces
}

/**
 * Generate Plotly traces for regression lines
 */
export const generateRegressionTraces = (
  options: PlotlyTraceOptions
): Partial<PlotData>[] => {
  const {
    regressionConfigs,
    regressionResults,
    chartData,
    xAxisSensorId,
    sensorAxisMapping,
    allSensorsToRender,
    enhancedSensorEntries,
    derivedValues,
  } = options

  const traces: Partial<PlotData>[] = []

  regressionConfigs.forEach((config) => {
    if (!config.enabled || !regressionResults[config.id]) return

    const regressionResult = regressionResults[config.id]

    const isDerivedRegression = config.yVariable.startsWith('derived_')
    let sensorDisplayName: string

    if (isDerivedRegression) {
      const derivedValue = derivedValues.find(
        (dv) => dv.id === config.yVariable
      )
      sensorDisplayName = derivedValue?.data.name || config.yVariable
    } else {
      const sensor = enhancedSensorEntries.find(
        (e) => e.id === config.yVariable
      )
      sensorDisplayName = sensor?.data?.name || config.yVariable
    }

    const sensorIndex = allSensorsToRender.findIndex(
      (s) => s.id === config.yVariable
    )
    if (sensorIndex === -1) return

    const lineColor = getSensorColor(sensorIndex)

    // Handle sensor-based regression
    if (
      xAxisSensorId &&
      config.regressionType === 'linear' &&
      config.xVariables?.length === 1
    ) {
      const independentSensorId = config.xVariables[0]
      // Only proceed if the independent sensor matches the x-axis
      if (independentSensorId === xAxisSensorId) {
        // Get x-axis values from the data
        const xValues = chartData
          .map((d) => d[xAxisSensorId])
          .filter((v): v is number => typeof v === 'number')
          .sort((a, b) => a - b)

        if (xValues.length === 0) return

        // Generate evenly spaced x values for smooth regression line
        const minX = xValues[0]
        const maxX = xValues[xValues.length - 1]
        const numPoints = DEFAULT_CHART_SETTINGS.NUM_REGRESSION_POINTS
        const smoothXValues: number[] = []

        for (let i = 0; i <= numPoints; i++) {
          smoothXValues.push(minX + (maxX - minX) * (i / numPoints))
        }

        // Use coefficients directly
        const coefficients = regressionResult.coefficients
        const intercept = coefficients[0]
        const slope = coefficients[1]

        const regressionData = smoothXValues.map((x) => {
          const y = intercept + slope * x
          return { x, y }
        })

        traces.push({
          type: 'scattergl',
          mode: 'lines',
          name: `${sensorDisplayName} (${config.type} fit)`,
          x: regressionData.map((d) => d.x),
          y: regressionData.map((d) => d.y),
          yaxis: sensorAxisMapping[config.yVariable]?.axis || 'y',
          line: {
            color: lineColor,
            width: 2,
            dash: 'dash',
          },
          connectgaps: true,
          hoverinfo: 'x+y',
          hovertemplate: `<b>%{x}</b><br>${config.type} fit: %{y}<extra></extra>`,
        })
      }
    }

    // Original time-based regression logic
    if (!xAxisSensorId && config.xVariables[0] === 'timestamp') {
      const dataTimestamps = chartData
        .map((d) => d.timestamp)
        .filter((t): t is number => t !== null)

      if (dataTimestamps.length === 0) return

      const minDataTime = dataTimestamps.reduce(
        (min, t) => (t < min ? t : min),
        dataTimestamps[0]
      )
      const maxDataTime = dataTimestamps.reduce(
        (max, t) => (t > max ? t : max),
        dataTimestamps[0]
      )
      const endTime = config.endDate
        ? new Date(config.endDate).getTime()
        : maxDataTime

      // Generate evenly spaced timestamps for smooth line
      const numPoints = DEFAULT_CHART_SETTINGS.NUM_REGRESSION_POINTS
      const allTimestamps: number[] = []
      for (let i = 0; i <= numPoints; i++) {
        const timestamp =
          minDataTime + (endTime - minDataTime) * (i / numPoints)
        allTimestamps.push(timestamp)
      }

      // Calculate regression values for all timestamps
      const regressionValues = predictValues(
        regressionResult,
        config,
        allTimestamps
      )

      traces.push({
        type: 'scattergl',
        mode: 'lines',
        name: `${sensorDisplayName} (${config.type} fit)`,
        x: allTimestamps.map((t) =>
          dayjs(t).utc().local().format('YYYY-MM-DD HH:mm:ss')
        ),
        y: regressionValues,
        yaxis: sensorAxisMapping[config.yVariable]?.axis || 'y',
        line: {
          color: lineColor,
          width: 2,
          dash: 'dash',
        },
        connectgaps: true,
        hoverinfo: 'x+y',
        hovertemplate: `<b>%{x|%Y-%m-%d %H:%M:%S}</b><br>${config.type} fit: %{y}<extra></extra>`,
      })
    }
  })

  return traces
}

/**
 * Generate all Plotly traces
 */
export const generatePlotlyData = (
  options: PlotlyTraceOptions
): Partial<PlotData>[] => {
  const sensorTraces = generateSensorTraces(options)
  const regressionTraces = generateRegressionTraces(options)
  return [...sensorTraces, ...regressionTraces]
}
